import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  Observable,
  debounceTime,
  distinctUntilChanged,
  finalize,
  filter,
  fromEvent,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { isPresent } from 'src/app/core/utils/isPresent';
import { ValidationErrorsService } from 'src/app/shared/services/validation-errors.service';

@UntilDestroy()
@Component({
  selector: 'app-single-select-autocomplete-and-create',
  templateUrl: './single-select-autocomplete-and-create.component.html',
  styleUrl: './single-select-autocomplete-and-create.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SingleSelectAutocompleteAndCreateComponent
  implements OnInit, ControlValueAccessor, AfterViewInit
{
  @Input() label: string | undefined;
  @Input() autocompleteOptions: any[] = [];
  @Input() optionIcon: string = '';
  @Input() optionIconColor: string = '';
  @Input() optionLabel: string = 'name';
  @Input() optionValue: string = 'id';
  @Input() placeholderKey: string = 'placeholders.select';
  @Input() createOptionMethod: ((value: string) => Observable<any>) | undefined;
  @Input() createErrorKey: string = 'validationError';

  @ViewChild('optionInput')
  optionInput!: ElementRef<HTMLInputElement>;

  @Output() onSelect: EventEmitter<string> = new EventEmitter<string>();
  selectedOptionValue: any;
  filteredOptions$: Observable<any[]> | undefined;
  focusOut$: Observable<any> | undefined;
  isCreating = false;
  currentSearchText = '';
  private pendingCreateText = '';
  private createdOptionValueToApply: any;

  constructor(
    @Self() @Optional() public ngControl: NgControl,
    private validationErrorsService: ValidationErrorsService,
    private cdr: ChangeDetectorRef,
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    this.selectedOptionValue = this.control.value;
    this.resetFilteredOptions();
  }

  ngAfterViewInit(): void {
    this.initSubscriptions();
  }

  initSubscriptions() {
    this.processValueChanges();
    this.processFocusOut();
  }

  resetFilteredOptions(): void {
    this.filteredOptions$ = this.control.valueChanges.pipe(
      startWith<string>(''),
      map((filterValue) => {
        const textValue =
          typeof filterValue === 'string' ? filterValue : this.displayFn(filterValue);
        this.currentSearchText = textValue || '';
        return this.filter(this.currentSearchText);
      }),
    );
  }

  processValueChanges(): void {
    this.control?.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged(), filter(isPresent))
      .subscribe((value) => {
        if (
          this.autocompleteOptions.findIndex(
            (item) => item[this.optionLabel] === value || item[this.optionValue] === value,
          ) === -1 &&
          this.selectedOptionValue
        ) {
          this.filteredOptions$ = this.control.valueChanges.pipe(
            startWith<string>(''),
            map((filterValue) => {
              const textValue =
                typeof filterValue === 'string' ? filterValue : this.displayFn(filterValue);
              this.currentSearchText = textValue || '';
              return this.filter(this.currentSearchText);
            }),
          );
          this.selectedOptionValue = undefined;
          this.onSelect.emit(undefined);
          this.control.patchValue(undefined);
          this.cdr.detectChanges();
        }
      });
  }

  processFocusOut(): void {
    this.focusOut$ = fromEvent(this.optionInput.nativeElement, 'blur');

    this.focusOut$.pipe(untilDestroyed(this), debounceTime(100)).subscribe(() => {
      setTimeout(() => {
        if (
          this.autocompleteOptions.findIndex((item) => {
            return item[this.optionLabel] === this.optionInput.nativeElement.value;
          }) === -1
        ) {
          this.control.patchValue(undefined);
        } else {
          this.resetFilteredOptions();
        }
      }, 100);
    });
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.selectedOptionValue = event.option.value;
    this.control.patchValue(event.option.value);
    this.onSelect.emit(this.selectedOptionValue);
    this.cdr.detectChanges();
  }

  setPendingCreateText(value: string): void {
    this.pendingCreateText = (value || '').trim();
  }

  createOption(): void {
    const optionName = (
      this.pendingCreateText ||
      this.optionInput?.nativeElement?.value ||
      this.currentSearchText
    ).trim();

    if (!this.createOptionMethod || !optionName) {
      return;
    }

    this.isCreating = true;
    this.pendingCreateText = '';
    this.control.disable({ emitEvent: false });
    this.cdr.markForCheck();

    this.createOptionMethod(optionName)
      .pipe(
        untilDestroyed(this),
        switchMap((createdOption) => {
          if (!createdOption) {
            return of(undefined);
          }

          this.autocompleteOptions = [...this.autocompleteOptions, createdOption];
          this.createdOptionValueToApply = createdOption[this.optionValue];
          this.resetFilteredOptions();

          return of(createdOption);
        }),
        tap({
          error: () => {
            this.control.setErrors({ [this.createErrorKey]: true });
            this.control.markAsTouched();
          },
        }),
        finalize(() => {
          this.isCreating = false;
          this.control.enable({ emitEvent: false });

          if (this.createdOptionValueToApply !== undefined) {
            this.selectedOptionValue = this.createdOptionValueToApply;
            this.control.patchValue(this.selectedOptionValue);
            this.control.setErrors(null);
            this.onSelect.emit(this.selectedOptionValue);
            this.createdOptionValueToApply = undefined;
          }

          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {},
        error: () => {},
      });
  }

  private filter(filterValue: string): any[] {
    if (!filterValue) {
      return [...this.autocompleteOptions].sort((a, b) =>
        a[this.optionLabel].localeCompare(b[this.optionLabel]),
      );
    }

    return this.autocompleteOptions
      .filter((option) => {
        return option[this.optionLabel].toLowerCase().indexOf(filterValue.toLowerCase()) >= 0;
      })
      .sort((a, b) => a[this.optionLabel].localeCompare(b[this.optionLabel]));
  }

  displayFn(value: any): string {
    if (!value) {
      return '';
    }

    return (
      this.autocompleteOptions.find((item) => {
        return item[this.optionValue] === value;
      })?.[this.optionLabel] || ''
    );
  }

  public getErrorMessage(): string {
    if (this.control?.invalid && this.control?.errors) {
      return this.validationErrorsService.getControlErrorMessage(this.control.errors);
    }
    return '';
  }

  public writeValue(value: any): void {}

  onChange: any = (value: string) => {};
  onTouched: any = () => {};

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  get control(): FormControl {
    return this.ngControl?.control as FormControl;
  }
  get isRequired(): boolean {
    const validator = this.control.validator ? this.control.validator({} as AbstractControl) : null;
    return !!(validator && validator['required']);
  }

  get canCreateOption(): boolean {
    const searchText = (this.currentSearchText || '').trim();

    if (!this.createOptionMethod || !searchText || this.isCreating) {
      return false;
    }

    return (
      this.autocompleteOptions.findIndex(
        (item) => item[this.optionLabel]?.toLowerCase() === searchText.toLowerCase(),
      ) === -1
    );
  }
}
